import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import LinkIcon from "@mui/icons-material/Link";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";

import SupplierEquipmentProductsApi from "../../../../../infrastructure/api/supplierEquipmentProducts";
import EquipmentMasterCatalogApi from "../../../../../infrastructure/api/equipmentMasterCatalog";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import EnhancedTable from "../../../../components/shared/table";
import EquipmentItemPicker from "../../shared/equipmentCatalog/EquipmentItemPicker";
import EquipmentPublicCodeChip from "../../shared/equipmentCatalog/EquipmentPublicCodeChip";

const EMPTY_COMMERCIAL = {
  supplierProductId: 0,
  unitPrice: 0,
  stockQuantity: 0,
  minOrderQuantity: 0,
  leadTimeDays: 0,
  warrantyMonths: 0,
  isAvailable: true,
  supplierInternalCode: "",
  priceValidUntil: null,
};

const EMPTY_REQUEST = {
  categoryId: "",
  brandText: "",
  modelText: "",
  description: "",
  datasheetUrl: "",
};

const SupplierEquipmentProductsPage = () => {
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);

  const [linkDialog, setLinkDialog] = useState({ open: false, product: null, picked: null });
  const [commercial, setCommercial] = useState({ open: false, form: EMPTY_COMMERCIAL });
  const [requestDialog, setRequestDialog] = useState({ open: false, form: EMPTY_REQUEST });

  const load = () =>
    SupplierEquipmentProductsApi.getMyProducts().then((r) => setRows(r?.data || []));

  useEffect(() => {
    load();
    EquipmentMasterCatalogApi.getPickerData().then((r) =>
      setCategories(r?.data?.categories || r?.data?.Categories || [])
    );
  }, []);

  const doLink = async () => {
    if (!linkDialog.picked) return toast.warn("یک تجهیز مرجع انتخاب کنید.");
    const res = await SupplierEquipmentProductsApi.linkToMaster({
      supplierProductId: linkDialog.product.id,
      equipmentItemId: linkDialog.picked.equipmentItemId,
      supplierInternalCode: linkDialog.product.supplierInternalCode || "",
    });
    if (res.ok) {
      toast.success("محصول به کاتالوگ مرجع متصل شد");
      setLinkDialog({ open: false, product: null, picked: null });
      load();
    } else toast.error(res.message || "خطا");
  };

  const openCommercial = (row) => {
    setCommercial({
      open: true,
      form: {
        supplierProductId: row.id,
        unitPrice: row.unitPrice || 0,
        stockQuantity: row.stockQuantity || 0,
        minOrderQuantity: row.minOrderQuantity || 0,
        leadTimeDays: row.leadTimeDays || 0,
        warrantyMonths: row.warrantyMonths || 0,
        isAvailable: !!row.isAvailable,
        supplierInternalCode: row.supplierInternalCode || "",
        priceValidUntil: row.priceValidUntil || null,
      },
    });
  };

  const saveCommercial = async () => {
    const res = await SupplierEquipmentProductsApi.updateCommercialInfo(commercial.form);
    if (res.ok) {
      toast.success("اطلاعات تجاری ذخیره شد");
      setCommercial({ open: false, form: EMPTY_COMMERCIAL });
      load();
    } else toast.error(res.message || "خطا");
  };

  const submitRequest = async () => {
    if (!requestDialog.form.categoryId) return toast.warn("دسته الزامی است.");
    const res = await SupplierEquipmentProductsApi.submitNewEquipmentRequest({
      ...requestDialog.form,
      categoryId: Number(requestDialog.form.categoryId),
    });
    if (res.ok) {
      toast.success("درخواست تجهیز جدید ثبت شد");
      setRequestDialog({ open: false, form: EMPTY_REQUEST });
    } else toast.error(res.message || "خطا");
  };

  const setC = (k, v) => setCommercial((c) => ({ ...c, form: { ...c.form, [k]: v } }));
  const setR = (k, v) => setRequestDialog((d) => ({ ...d, form: { ...d.form, [k]: v } }));

  const headCells = [
    { id: "productName", label: "نام محصول" },
    { id: "brand", label: "برند" },
    { id: "model", label: "مدل" },
    {
      id: "equipmentPublicCode",
      label: "کد مرجع",
      render: (value) =>
        value ? <EquipmentPublicCodeChip code={value} /> : "—",
    },
    { id: "unitPrice", label: "قیمت واحد", type: "price" },
    { id: "stockQuantity", label: "موجودی" },
  ];

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <BreadcrumbsComponent />
      </Grid>
      <Grid item xs={12}>
        <Card>
          <CardHeader
            title="محصولات تجهیزات تأمین‌کننده"
            action={
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setRequestDialog({ open: true, form: EMPTY_REQUEST })}
              >
                درخواست تجهیز جدید
              </Button>
            }
          />
          <CardContent>
            <EnhancedTable
              rowKey={"id"}
              rows={rows}
              headCells={headCells}
              pageSize={100}
              actions={[
                {
                  icon: <LinkIcon sx={{ width: "1rem", height: "1rem" }} />,
                  onClick: (row) => setLinkDialog({ open: true, product: row, picked: null }),
                  color: "primary",
                  tooltip: "اتصال به کاتالوگ مرجع",
                },
                {
                  icon: <EditIcon sx={{ width: "1rem", height: "1rem" }} />,
                  onClick: openCommercial,
                  color: "secondary",
                  tooltip: "ویرایش اطلاعات تجاری",
                },
              ]}
            />
          </CardContent>
        </Card>
      </Grid>

      {/* Link to master dialog */}
      <Dialog
        open={linkDialog.open}
        onClose={() => setLinkDialog({ open: false, product: null, picked: null })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>اتصال محصول به کاتالوگ مرجع</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <EquipmentItemPicker
            value={linkDialog.picked}
            onChange={(v) => setLinkDialog((d) => ({ ...d, picked: v }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialog({ open: false, product: null, picked: null })}>
            انصراف
          </Button>
          <Button variant="contained" onClick={doLink}>
            اتصال
          </Button>
        </DialogActions>
      </Dialog>

      {/* Commercial info dialog */}
      <Dialog
        open={commercial.open}
        onClose={() => setCommercial({ open: false, form: EMPTY_COMMERCIAL })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>ویرایش اطلاعات تجاری</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="قیمت واحد"
                value={commercial.form.unitPrice}
                onChange={(e) => setC("unitPrice", Number(e.target.value))}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="موجودی"
                value={commercial.form.stockQuantity}
                onChange={(e) => setC("stockQuantity", Number(e.target.value))}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="حداقل سفارش"
                value={commercial.form.minOrderQuantity}
                onChange={(e) => setC("minOrderQuantity", Number(e.target.value))}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="زمان تحویل (روز)"
                value={commercial.form.leadTimeDays}
                onChange={(e) => setC("leadTimeDays", Number(e.target.value))}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="گارانتی (ماه)"
                value={commercial.form.warrantyMonths}
                onChange={(e) => setC("warrantyMonths", Number(e.target.value))}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="کد داخلی تأمین‌کننده"
                value={commercial.form.supplierInternalCode}
                onChange={(e) => setC("supplierInternalCode", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!commercial.form.isAvailable}
                    onChange={(e) => setC("isAvailable", e.target.checked)}
                  />
                }
                label="موجود / قابل ارائه"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommercial({ open: false, form: EMPTY_COMMERCIAL })}>
            انصراف
          </Button>
          <Button variant="contained" onClick={saveCommercial}>
            ذخیره
          </Button>
        </DialogActions>
      </Dialog>

      {/* New equipment request dialog */}
      <Dialog
        open={requestDialog.open}
        onClose={() => setRequestDialog({ open: false, form: EMPTY_REQUEST })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>درخواست افزودن تجهیز به کاتالوگ مرجع</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              size="small"
              label="دسته *"
              SelectProps={{ native: true }}
              value={requestDialog.form.categoryId}
              onChange={(e) => setR("categoryId", e.target.value)}
            >
              <option value="">—</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.titleFa} ({c.codePrefix})
                </option>
              ))}
            </TextField>
            <TextField
              size="small"
              label="برند"
              value={requestDialog.form.brandText}
              onChange={(e) => setR("brandText", e.target.value)}
            />
            <TextField
              size="small"
              label="مدل"
              value={requestDialog.form.modelText}
              onChange={(e) => setR("modelText", e.target.value)}
            />
            <TextField
              size="small"
              multiline
              minRows={2}
              label="توضیحات"
              value={requestDialog.form.description}
              onChange={(e) => setR("description", e.target.value)}
            />
            <TextField
              size="small"
              label="لینک دیتاشیت"
              value={requestDialog.form.datasheetUrl}
              onChange={(e) => setR("datasheetUrl", e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestDialog({ open: false, form: EMPTY_REQUEST })}>
            انصراف
          </Button>
          <Button variant="contained" onClick={submitRequest}>
            ثبت درخواست
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default SupplierEquipmentProductsPage;
